import React from 'react';
import type { MenuNode } from '@milanote/roma/application';
import { ActionMenu } from '@milanote/roma/application';
import { useSheetClose } from '@milanote/roma';

type MobileRomaMenuProps = {
    ariaLabel: string;
    nodes: MenuNode[];
    showIcons?: boolean;
};

/** Adds sheet dismissal to a row and, recursively, to any submenu rows it owns. */
const withSheetDismissal = (node: MenuNode, closeSheet: () => void): MenuNode => {
    if (node.type === 'divider') return node;

    return {
        ...node,
        children: node.children?.map((child) => withSheetDismissal(child, closeSheet)),
        onAction: node.onAction
            ? () => {
                  node.onAction?.();
                  if (!node.keepOpenOnSelect) closeSheet();
              }
            : undefined,
    };
};

/**
 * Prepares resolved nodes for the mobile sheet. The sheet design rules off every row, so the
 * resolved section dividers give way to one after each item; submenus keep their popover dividers.
 */
const toMobileSheetNodes = (nodes: MenuNode[], closeSheet: () => void): MenuNode[] =>
    nodes
        .filter((node) => node.type !== 'divider')
        .flatMap((node) => [
            withSheetDismissal(node, closeSheet),
            { type: 'divider' as const, id: `divider-after-${node.id}` },
        ]);

/** Renders resolved menu nodes with the shared Roma context-menu component inside a mobile sheet. */
const MobileRomaMenu: React.FC<MobileRomaMenuProps> = ({ ariaLabel, nodes, showIcons = false }) => {
    const closeSheet = useSheetClose();
    const mobileNodes = toMobileSheetNodes(nodes, closeSheet);

    return (
        <ActionMenu.Content
            aria-label={ariaLabel}
            nodes={mobileNodes}
            size="lg"
            className="min-h-18 pt-md"
            showIcons={showIcons}
            showShortcuts={false}
        />
    );
};

export default MobileRomaMenu;
